/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react'
import { Card, CardHeader, IconButton, Box } from '@mui/material'
import { useHookstate } from '@hookstate/core'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import dayjs, { Dayjs } from 'dayjs'
import { useLang } from '~/hooks/useLang'
import useSystemState from '~/features/System/stores/SystemState'
import { CurrentCoordination } from '~/components/FilterSwitchCoordination/interface'
import DispatchByDeviceCharts from '../DispatchByDeviceCharts'
import DispatchByLocationCharts from '../DispatchByLocationCharts'
import DispatchByTimeAndStatusCharts from '../DispatchByTimeAndStatusCharts'
import DispatchByTotalByStatusCards from '../DispatchByTotalByStatusCards'
import DispatchByTypeAndSubtypeCharts from '../DispatchByTypeAndSubtypeCharts'
import DispatchFiltersDashboard from '../DispatchFiltersDashboard'
import DispatchHotMap from '../DispatchHotMap'
import DispatchByTimeChart from '../DispatchByTimeChart'
import AverageDispatchClosingTime from '../AverageDispatchClosingTime'
import { useMarkerState } from '~/features/Map/stores/MapState'
import type { IGeographical } from '~/features/Map/components/FilterGeographicalArea/interfaces'
import ExportDispatchesButton from '~/components/ExportDispatchesButton'

export interface DispatchClosedProps {
  readonly id: number
  readonly name: string
  readonly acronym: string
  readonly category_id?: number
}

/* eslint-disable max-lines-per-function */
export default function DispatchCardDashboard() {
  const { translate } = useLang()
  const geographicalArea = useHookstate<IGeographical[]>([])
  const refresh = useHookstate<boolean>(false)
  const loading = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const lockFields = useHookstate<boolean>(false)
  const currentCoordination = useHookstate<CurrentCoordination | null>(null)
  const { systemConfig } = useSystemState()
  const withSolvedThirdParty = useHookstate<boolean>(false)

  const categoryIds = useHookstate<readonly number[]>([])
  const agencyId = useHookstate<number | null>(null)
  const deviceId = useHookstate<number | null>(null)
  const groupId = useHookstate<readonly number[]>([])
  const operationId = useHookstate<number | null>(null)

  const typeIds = useHookstate<readonly number[]>([])
  const subtypeIds = useHookstate<readonly number[]>([])
  const refTypeIds = useHookstate<readonly number[] | readonly []>([])
  const initialPeriod = useHookstate<string>('3')
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(24, 'hours')
  )
  const [finishDate, setFinishDate] = useState<Dayjs | null>(dayjs())

  const consummatedAndAttempted = useHookstate<string | null>(null)
  const statusIds = useHookstate<readonly number[]>([])
  const cityIds = useHookstate<readonly number[]>([])
  const districtIds = useHookstate<readonly number[]>([])
  const dispatchGroupSetId = useHookstate<number | null>(null)
  const location = useHookstate<string>('')
  const operationUnitId = useHookstate<number | null>(null)
  const closedStatusClassId = useHookstate<number | null>(null)
  const closedStatusId = useHookstate<number | null>(null)
  const qualifiersSelected = useHookstate<readonly number[]>([])
  const clearState = useHookstate<boolean>(false)
  const searched = useHookstate<boolean>(false)
  const requester = useHookstate<string | null>(null)
  const openModalGeographicalArea = useHookstate<boolean>(false)
  const phone = useHookstate<string | null>(null)
  const userId = useHookstate<number | null>(null)
  const focusChangeUserId = useHookstate<boolean>(false)
  const resetValueUserId = useHookstate(false)
  const locationAccuracy = useHookstate<readonly number[]>([])
  const selectIsValidatedTypeMap = useHookstate<string | null>('1')
  
  const timeIntervalEnabled = useHookstate<boolean>(false)
  const startTime = useHookstate<string>('')
  const endTime = useHookstate<string>('')

  const disabledSendButton = () => {
    if (!startDate) return true
    if (timeIntervalEnabled.get() && (!startTime.get() || !endTime.get())) return true
    if (timeIntervalEnabled.get() && startTime.get() && endTime.get() && startTime.get() === endTime.get()) return true
    return false
  }

  const hasFiltersSelected = () => {
    return startDate !== null && finishDate !== null
  }

  useEffect(() => {
    document.title = translate('Dispatch Dashboard - Dispatch System')
  }, [])

  return (
    <Card
      sx={{
        boxShadow: 4,
        paddingBottom: 2,
        height: expandFilterSearch.value ? 'auto' : 50,
      }}
    >
      <CardHeader
        title={`${translate('Dispatch')}`}
        avatar={
          <IconButton
            onClick={() => {
              expandFilterSearch.set(!expandFilterSearch.get())
            }}
            sx={{
              display: 'flex',
              justifyContent: 'end',
              marginRight: 1,
              padding: 0,
            }}
          >
            {expandFilterSearch.value ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        action={
          <Box sx={{ mr: 2 }}>
            {systemConfig().featureToPocBa.get() === '1' && (
              <ExportDispatchesButton
                disabled={
                  !hasFiltersSelected() || loading.get() || !searched.get()
                }
                filters={{
                  startDate: startDate?.format('YYYY-MM-DD HH:mm:ss') || '',
                  finishDate: finishDate?.toDate() || new Date(),
                  excludeClosed: closedStatusId.get() !== null,
                  agencyId: agencyId.get() || undefined,
                  dispatchGroupIds: groupId.get(),
                  typeIds: typeIds.get(),
                  subtypeIds: subtypeIds.get(),
                  categoryIds: categoryIds.get(),
                  operationId: operationId.get() || undefined,
                  withSolvedThirdParty: withSolvedThirdParty.get(),
                  deviceId: deviceId.get() || undefined,
                  consummated:
                    consummatedAndAttempted.get() === null
                      ? null
                      : consummatedAndAttempted.get() === '1',
                  statusIds: statusIds.get(),
                  cityIds: cityIds.get(),
                  groupSetId: dispatchGroupSetId.get() || undefined,
                  location: location.get() || undefined,
                  closedStatusClassId: closedStatusClassId.get() || undefined,
                  closedStatusId: closedStatusId.get() || undefined,
                  qualifierOptions: qualifiersSelected.get(),
                  operationUnitId: operationUnitId.get() || undefined,
                  name: requester.get() || undefined,
                  phone: phone.get() || undefined,
                  userId: userId.get() || undefined,
                  geographicAreas: geographicalArea.get(),
                  districtIds: districtIds.get(),
                }}
              />
            )}
          </Box>
        }
        titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
      />
      <DispatchFiltersDashboard
        loading={loading}
        currentCoordination={currentCoordination}
        withSolvedThirdParty={withSolvedThirdParty}
        deviceId={deviceId}
        groupId={groupId}
        agencyId={agencyId}
        categoryIds={categoryIds}
        typeIds={typeIds}
        subtypeIds={subtypeIds}
        refTypeIds={refTypeIds}
        initialPeriod={initialPeriod}
        operationId={operationId}
        phone={phone}
        requester={requester}
        onStartDateChange={(value) => setStartDate(value)}
        onFinishDateChange={(value) => setFinishDate(value)}
        onPeriodChange={(value) => initialPeriod.set(value)}
        timeIntervalEnabled={timeIntervalEnabled}
        startTime={startTime}
        endTime={endTime}
        hidden={expandFilterSearch.value}
        lockFields={lockFields.get()}
        disabledSendButton={disabledSendButton}
        consummatedAndAttempted={consummatedAndAttempted}
        openModalGeographicalArea={openModalGeographicalArea}
        setCoordinationDates={(checked) => {
          if (checked) {
            groupId.set(
              currentCoordination
                .get()
                ?.dispatch_groups.map((group) => group.id) ?? []
            )
            initialPeriod.set(
              'c' +
                currentCoordination.get()?.date_begin +
                '&' +
                currentCoordination.get()?.date_expected_closing
            )

            lockFields.set(true)
          } else {
            groupId.set([])
            initialPeriod.set('')
            lockFields.set(false)
          }
        }}
        clearFilters={() => {
          currentCoordination.set(null)
          withSolvedThirdParty.set(false)
          deviceId.set(null)
          groupId.set([])
          agencyId.set(null)
          refTypeIds.set([])
          groupId.set([])
          categoryIds.set([])
          typeIds.set([])
          subtypeIds.set([])
          initialPeriod.set('3')
          operationId.set(null)
          consummatedAndAttempted.set(null)
          statusIds.set([])
          cityIds.set([])
          districtIds.set([])
          dispatchGroupSetId.set(null)
          location.set('')
          operationUnitId.set(null)
          closedStatusClassId.set(null)
          closedStatusId.set(null)
          qualifiersSelected.set([])
          clearState.set(!clearState.get())
          searched.set(false)
          phone.set(null)
          geographicalArea.set([])
          requester.set(null)
          userId.set(null)
          locationAccuracy.set([])
          selectIsValidatedTypeMap.set('1')
          timeIntervalEnabled.set(false)
          startTime.set('')
          endTime.set('')
        }}
        searchButton={() => {
          refresh.set(!refresh.get())
          searched.set(true)
        }}
        geographicalArea={geographicalArea}
        statusIds={statusIds}
        cityIds={cityIds}
        districtIds={districtIds}
        dispatchGroupSetId={dispatchGroupSetId}
        location={location}
        operationUnitId={operationUnitId}
        closedStatusClassId={closedStatusClassId}
        closedStatusId={closedStatusId}
        qualifiersSelected={qualifiersSelected}
        clearState={clearState}
        userId={userId}
        focusChangeUserId={focusChangeUserId}
        resetValueUserId={resetValueUserId}
      />
      {searched.get() && (
        <>
          <DispatchByTotalByStatusCards
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            operationId={operationId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            operationUnitId={operationUnitId.get()}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            phone={phone.get()}
            requester={requester.get()}
            userId={userId.get()}
            expandFilterSearch={expandFilterSearch.value}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <AverageDispatchClosingTime
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            refresh={refresh.get()}
            operationId={operationId.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            operationUnitId={operationUnitId.get()}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <DispatchByTypeAndSubtypeCharts
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            refresh={refresh.get()}
            operationId={operationId.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            operationUnitId={operationUnitId.get()}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <DispatchByTimeAndStatusCharts
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            operationId={operationId.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            operationUnitId={operationUnitId.get()}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <DispatchByLocationCharts
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            operationId={operationId.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            location={location.get() ? location.get() : null}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            groupSetId={dispatchGroupSetId.get()}
            operationUnitId={operationUnitId.get()}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <DispatchByDeviceCharts
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            operationId={operationId.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            operationUnitId={operationUnitId.get()}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />

          <DispatchByTimeChart
            loading={loading}
            searched={searched.get()}
            agencyId={agencyId.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            operationId={operationId.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            operationUnitId={operationUnitId.get()}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />
          <DispatchHotMap
            agencyId={agencyId.get()}
            searched={searched.get()}
            deviceId={deviceId.get()}
            dispatchGroupId={groupId.get()}
            category={categoryIds.get()}
            type={typeIds.get()}
            subtype={subtypeIds.get()}
            dateStart={startDate?.toDate() ?? null}
            dateFinish={finishDate?.toDate() ?? null}
            thirdParty={withSolvedThirdParty.get()}
            operationId={operationId.get()}
            refresh={refresh.get()}
            consummated={consummatedAndAttempted.get()}
            statusIds={statusIds.get()}
            cityIds={cityIds.get()}
            districtIds={districtIds.get()}
            groupSetId={dispatchGroupSetId.get()}
            location={location.get() ? location.get() : null}
            closedStatusClassId={closedStatusClassId.get()}
            closedStatusId={closedStatusId.get()}
            qualifierOptions={qualifiersSelected.get()}
            expandFilterSearch={expandFilterSearch.value}
            operationUnitId={operationUnitId.get()}
            phone={phone.get()}
            userId={userId.get()}
            requester={requester.get()}
            geographicalArea={geographicalArea.get()}
            locationAccuracy={locationAccuracy}
            selectIsValidatedTypeMap={selectIsValidatedTypeMap}
            startTime={timeIntervalEnabled.get() ? startTime.get() : null}
            endTime={timeIntervalEnabled.get() ? endTime.get() : null}
          />
        </>
      )}
    </Card>
  )
}
